/* ============================================================
   Nasher Miles — floating chat launcher
   Save as: assets/nm-chat.js
   Load with: <script src="{{ 'nm-chat.js' | asset_url }}" defer></script>

   Pairs with the Robylon loader in <head>, which must capture the
   instance, hide their launcher, and forward the unread count:

     s.onload=function(){if(a){window.__nmBot=window.RobylonChatbot
       .create({api_key:a,show_launcher:false,
         onUnreadCountChange:function(c){window.__nmUnread=c;
           try{window.dispatchEvent(new CustomEvent("nm:unread",{detail:c}))}catch(e){}}})}};

   create() returns { show, hide, toggle, destroy, getState } — that
   instance is the only control surface. window.R is a queue the SDK
   never reads, so it is not used here.
   ============================================================ */
(function () {
  'use strict';

  var WHATSAPP = 'https://api.whatsapp.com/send?phone=919930076456&text=Hi%2C%20I%20have%20a%20service%20query';
  var FALLBACK = 'https://chatbot.robylon.ai/chatbot-plugin?id=9023fc55-6424-480c-b20b-4217689fdaa4';
  var READY_WAIT = 8000;   // their SDK initialises on window.load
  var DEBUG = false;

  if (window.__nmChatLoaded) return;
  window.__nmChatLoaded = true;

  function log() {
    if (DEBUG && window.console) console.log.apply(console, ['[nm-chat]'].concat([].slice.call(arguments)));
  }

  /* ---------------- styles ---------------- */
  /* No rules target Robylon's iframe — show_launcher:false handles it. */

  var CSS = [
    '.nm-chat{',
    '--nm-ink:#0B0B0B;--nm-ink-soft:#5B5B5B;--nm-surface:#fff;--nm-line:#E6E2DC;',
    '--nm-accent:#FF5A23;--nm-accent-dark:#E84C18;--nm-accent-tint:#FFF7F2;',
    '--nm-whatsapp:#25D366;--nm-badge:#E5252A;--nm-shadow:0 10px 28px rgba(11,11,11,.14);',
    'position:fixed;right:18px;bottom:18px;z-index:2000000;',
    'display:flex;flex-direction:column;align-items:flex-end;gap:10px;',
    'font-family:"Instrument Sans",var(--FONT-STACK-BODY,inherit),sans-serif}',

    '.nm-chat__menu{display:flex;flex-direction:column;gap:8px;margin:0;padding:0;list-style:none}',
    '.nm-chat__menu[hidden]{display:none}',

    '.nm-chat__option{display:flex;align-items:center;gap:12px;width:268px;',
    'padding:11px 16px 11px 12px;background:var(--nm-surface);border:1px solid var(--nm-line);',
    'border-radius:12px;box-shadow:var(--nm-shadow);color:var(--nm-ink);text-decoration:none;',
    'text-align:left;cursor:pointer;font:inherit;transform:translateY(8px);opacity:0;',
    'animation:nm-rise .22s cubic-bezier(.2,.9,.3,1) forwards}',
    '.nm-chat__menu li:nth-child(2) .nm-chat__option{animation-delay:.05s}',
    '.nm-chat__option--ai:hover{border-color:var(--nm-accent);background:var(--nm-accent-tint)}',
    '.nm-chat__option--service:hover{border-color:var(--nm-whatsapp);background:#F3FCF6}',
    '.nm-chat__option:focus-visible{outline:2px solid var(--nm-ink);outline-offset:2px}',
    '.nm-chat__option--busy{opacity:.6;pointer-events:none}',
    '@keyframes nm-rise{to{transform:translateY(0);opacity:1}}',

    '.nm-chat__icon{flex:0 0 36px;height:36px;display:grid;place-items:center;',
    'border-radius:9px;color:#fff}',
    '.nm-chat__option--ai .nm-chat__icon{background:var(--nm-accent)}',
    '.nm-chat__option--service .nm-chat__icon{background:var(--nm-whatsapp)}',
    '.nm-chat__icon svg{width:24px;height:24px}',
    '.nm-chat__option--service .nm-chat__icon svg{width:20px;height:20px}',

    '.nm-chat__label{display:block;font-size:15px;font-weight:600;line-height:1.25}',
    '.nm-chat__sub{display:block;margin-top:2px;font-size:12.5px;color:var(--nm-ink-soft);line-height:1.3}',

    '.nm-chat__toggle{position:relative;width:58px;height:58px;display:grid;place-items:center;border:0;',
    'border-radius:50%;background:var(--nm-accent);color:#fff;cursor:pointer;',
    'box-shadow:var(--nm-shadow);transition:background-color .18s ease,transform .18s ease}',
    '.nm-chat__toggle:hover{background:var(--nm-accent-dark);transform:scale(1.04)}',
    '.nm-chat__toggle:focus-visible{outline:2px solid var(--nm-ink);outline-offset:3px}',
    '.nm-chat__toggle svg{width:26px;height:26px}',
    '.nm-chat__toggle .nm-chat__dot{fill:var(--nm-accent);transition:fill .18s ease}',
    '.nm-chat__toggle:hover .nm-chat__dot{fill:var(--nm-accent-dark)}',
    '.nm-chat__toggle .nm-chat__close{display:none}',
    '.nm-chat--open .nm-chat__toggle .nm-chat__open{display:none}',
    '.nm-chat--open .nm-chat__toggle .nm-chat__close{display:block}',
    '.nm-chat--chat .nm-chat__menu{display:none}',

    /* unread badge */
    '.nm-chat__badge{position:absolute;top:-3px;right:-3px;min-width:20px;height:20px;',
    'padding:0 6px;box-sizing:border-box;border-radius:10px;background:var(--nm-badge);',
    'color:#fff;font-size:11.5px;font-weight:700;line-height:20px;text-align:center;',
    'box-shadow:0 0 0 2px #fff;pointer-events:none;animation:nm-pop .25s cubic-bezier(.2,.9,.3,1.4)}',
    '.nm-chat__badge[hidden]{display:none}',
    '.nm-chat--chat .nm-chat__badge{display:none}',
    '@keyframes nm-pop{from{transform:scale(.4);opacity:0}to{transform:scale(1);opacity:1}}',

    '@media (max-width:600px){.nm-chat{right:12px;bottom:76px}',
    '.nm-chat__option{width:min(272px,calc(100vw - 32px))}}',
    '@media (prefers-reduced-motion:reduce){',
    '.nm-chat__option{animation:none;opacity:1;transform:none}',
    '.nm-chat__badge{animation:none}',
    '.nm-chat__toggle{transition:none}.nm-chat__toggle:hover{transform:none}}'
  ].join('');

  function injectStyles() {
    if (document.getElementById('nm-chat-styles')) return;
    var s = document.createElement('style');
    s.id = 'nm-chat-styles';
    s.appendChild(document.createTextNode(CSS));
    (document.head || document.documentElement).appendChild(s);
  }

  /* ---------------- markup ---------------- */

  var AI_ICON =
       '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M7.5 6V2.6h5V6"/>' +
    '<rect x="3.5" y="6" width="13" height="14" rx="2.5"/>' +
    '<path d="M7.8 9.5v7M12.2 9.5v7"/>' +
    '<circle cx="6.5" cy="21.9" r=".9"/>' +
    '<circle cx="13.5" cy="21.9" r=".9"/>' +
    '<path d="M20 1.3c.26 1.36.57 1.94 2.2 2.2-1.63.26-1.94.57-2.2 2.2-.26-1.63-.57-1.94-2.2-2.2 1.63-.26 1.94-.84 2.2-2.2z" fill="currentColor" stroke-width=".9"/>' +
    '<path d="M21.2 7.6c.14.74.31 1.06 1.2 1.2-.89.14-1.06.31-1.2 1.2-.14-.89-.31-1.06-1.2-1.2.89-.14 1.06-.46 1.2-1.2z" fill="currentColor" stroke-width=".7"/></svg>';

  var SERVICE_ICON =
    '<svg viewBox="0 0 24 24" fill="currentColor">' +
    '<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>';

  var BUBBLE_ICON =
    '<svg class="nm-chat__open" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    '<path d="M5.4 3h13.2A2.4 2.4 0 0 1 21 5.4v9.2a2.4 2.4 0 0 1-2.4 2.4H9.9l-4.1 3.3c-.5.4-1.3.05-1.3-.6V17A2.4 2.4 0 0 1 3 14.6V5.4A2.4 2.4 0 0 1 5.4 3z"/>' +
    '<circle class="nm-chat__dot" cx="8.4" cy="10" r="1.15"/>' +
    '<circle class="nm-chat__dot" cx="12" cy="10" r="1.15"/>' +
    '<circle class="nm-chat__dot" cx="15.6" cy="10" r="1.15"/></svg>' +
    '<svg class="nm-chat__close" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  var MARKUP =
    '<ul class="nm-chat__menu" id="nm-chat-menu" hidden>' +
      '<li><a class="nm-chat__option nm-chat__option--ai" data-nm-action="bot" ' +
        'href="' + FALLBACK + '" target="_blank" rel="noopener">' +
        '<span class="nm-chat__icon" aria-hidden="true">' + AI_ICON + '</span>' +
        '<span><span class="nm-chat__label">AI Shopping Assistant</span>' +
        '<span class="nm-chat__sub">Find the right bag for your trip</span></span></a></li>' +
      '<li><a class="nm-chat__option nm-chat__option--service" ' +
        'href="' + WHATSAPP + '" target="_blank" rel="noopener">' +
        '<span class="nm-chat__icon" aria-hidden="true">' + SERVICE_ICON + '</span>' +
        '<span><span class="nm-chat__label">Service Query</span>' +
        '<span class="nm-chat__sub">Orders, returns and warranty</span></span></a></li>' +
    '</ul>' +
    '<button class="nm-chat__toggle" id="nm-chat-toggle" type="button" aria-expanded="false" ' +
      'aria-controls="nm-chat-menu" aria-label="Open help menu">' + BUBBLE_ICON +
      '<span class="nm-chat__badge" id="nm-chat-badge" aria-hidden="true" hidden></span>' +
    '</button>';

  var root, btn, menu, badge, chatOpen = false, waiting = null, poll = null, unread = 0;

  function build() {
    root = document.getElementById('nm-chat');
    if (!root) {
      root = document.createElement('div');
      root.className = 'nm-chat';
      root.id = 'nm-chat';
      root.innerHTML = MARKUP;
      document.body.appendChild(root);
    }
    btn = document.getElementById('nm-chat-toggle');
    menu = document.getElementById('nm-chat-menu');
    badge = document.getElementById('nm-chat-badge');
    return !!(root && btn && menu);
  }

  /* Button label, with the unread count appended for screen readers. */
  function setLabel(base) {
    var extra = unread > 0 ? ', ' + unread + ' unread message' + (unread === 1 ? '' : 's') : '';
    btn.setAttribute('aria-label', base + extra);
  }

  /* ---------------- unread badge ---------------- */

  /* Fed by the loader's onUnreadCountChange (alpha.19+). Robylon sends
     0 while the chat is open and clears it when the chat is opened. */
  function setUnread(n) {
    n = parseInt(n, 10);
    if (!(n > 0)) n = 0;
    unread = n;
    if (badge) {
      badge.textContent = n > 9 ? '9+' : String(n);
      badge.hidden = n === 0;
    }
    setLabel(chatOpen ? 'Close chat' : (menu && !menu.hidden ? 'Close help menu' : 'Open help menu'));
    log('unread', n);
  }

  /* ---------------- Robylon ---------------- */

  /* The instance captured by the loader. window.RobylonChatbot itself
     only exposes create/getState, so it is not a usable fallback. */
  function bot() {
    var b = window.__nmBot;
    return b && typeof b.show === 'function' ? b : null;
  }

  function markOpen() {
    chatOpen = true;
    root.classList.add('nm-chat--open', 'nm-chat--chat');
    setLabel('Close chat');
    watch();
  }

  function markClosed() {
    chatOpen = false;
    root.classList.remove('nm-chat--open', 'nm-chat--chat');
    setLabel('Open help menu');
    if (poll) { window.clearInterval(poll); poll = null; }
  }

  /* Their SDK initialises on window.load, so a fast click can arrive
     before the instance exists. Wait for it rather than failing. */
  function openChat(onFail) {
    if (waiting) return;
    var b = bot();
    if (b) { b.show(); markOpen(); log('shown'); return; }

    var started = Date.now();
    waiting = window.setInterval(function () {
      var ready = bot();
      if (ready) {
        window.clearInterval(waiting);
        waiting = null;
        ready.show();
        markOpen();
        log('shown after wait');
        return;
      }
      if (Date.now() - started > READY_WAIT) {
        window.clearInterval(waiting);
        waiting = null;
        log('instance never appeared');
        if (onFail) onFail();
      }
    }, 200);
  }

  function closeChat() {
    if (waiting) { window.clearInterval(waiting); waiting = null; }
    var b = bot();
    if (b) { try { b.hide(); } catch (err) { log('hide failed', err); } }
    markClosed();
  }

  /* Is Robylon's panel open? true / false, or null if we can't tell. */
  function readState(s) {
    if (typeof s === 'boolean') return s;
    if (typeof s === 'string') {
      if (/close|hidden|hide|minimi|collapsed/i.test(s)) return false;
      if (/open|show|visible|expand/i.test(s)) return true;
      return null;
    }
    if (s && typeof s === 'object') {
      var keys = ['isOpen', 'open', 'opened', 'visible', 'isVisible', 'expanded', 'isExpanded', 'chatOpen'];
      for (var i = 0; i < keys.length; i++) {
        if (typeof s[keys[i]] === 'boolean') return s[keys[i]];
      }
      var nested = s.state || s.status || s.mode || s.view;
      if (nested !== undefined) return readState(nested);
    }
    return null;
  }

  /* Fallback: is a Robylon iframe visibly taking up chat-panel space? */
  function panelVisible() {
    var frames = document.querySelectorAll('iframe[src*="robylon"], iframe[id*="robylon" i], iframe[class*="robylon" i]');
    if (!frames.length) return null;
    for (var i = 0; i < frames.length; i++) {
      var f = frames[i], r = f.getBoundingClientRect(), cs = window.getComputedStyle(f);
      if (r.width > 120 && r.height > 200 && cs.display !== 'none' &&
          cs.visibility !== 'hidden' && parseFloat(cs.opacity) > 0.05) return true;
    }
    return false;
  }

  function botIsOpen() {
    var b = bot(), open = null;
    if (b && typeof b.getState === 'function') {
      try { open = readState(b.getState()); } catch (err) { open = null; }
    }
    if (open === null) open = panelVisible();
    return open;
  }

  /* If they close the panel with Robylon's own control, resync our button. */
  function watch() {
    if (poll) window.clearInterval(poll);
    var grace = Date.now() + 1200;   // give the panel time to open
    poll = window.setInterval(function () {
      if (!chatOpen || Date.now() < grace) return;
      if (botIsOpen() === false) { markClosed(); log('closed via Robylon control'); }
    }, 500);
  }
  /* ---------------- behaviour ---------------- */

  function setMenu(open) {
    menu.hidden = !open;
    btn.setAttribute('aria-expanded', String(open));
    root.classList.toggle('nm-chat--open', open);
    setLabel(open ? 'Close help menu' : 'Open help menu');
  }

  function wire() {
    btn.addEventListener('click', function () {
      if (chatOpen) { closeChat(); setMenu(false); return; }
      setMenu(menu.hidden);
    });

    menu.addEventListener('click', function (e) {
      var el = e.target.closest ? e.target.closest('[data-nm-action="bot"]') : null;
      if (!el) { setMenu(false); return; }     // WhatsApp link, let it through
      e.preventDefault();
      el.classList.add('nm-chat__option--busy');
      setMenu(false);
      openChat(function () {
        el.classList.remove('nm-chat__option--busy');
        window.open(el.href, '_blank', 'noopener');
      });
      window.setTimeout(function () { el.classList.remove('nm-chat__option--busy'); }, 400);
    });

    document.addEventListener('click', function (e) {
      if (!root.contains(e.target) && !menu.hidden) setMenu(false);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (chatOpen) { closeChat(); btn.focus(); }
      else if (!menu.hidden) { setMenu(false); btn.focus(); }
    });

            /* AI Mode pill in the header search bar opens the assistant */
    document.addEventListener('click', function (e) {
      var ai = e.target.closest ? e.target.closest('[data-ai-mode]') : null;
      if (!ai) return;
      e.preventDefault();
      setMenu(false);

      // Close the search popdown so its overlay/scroll-lock doesn't sit behind the chat
     var holder = ai.closest('.search-form__input-holder, .predictive-search__input__container');
      var closeBtn = holder && holder.querySelector('[data-popdown-close], .search-bar-close__button');
      if (closeBtn) closeBtn.click();

      ai.classList.add('search-form__ai-mode--busy');
      openChat(function () {
        ai.classList.remove('search-form__ai-mode--busy');
        window.open(FALLBACK, '_blank', 'noopener');
      });
      window.setTimeout(function () { ai.classList.remove('search-form__ai-mode--busy'); }, 400);
    });

    /* Unread count from the Robylon loader */
    window.addEventListener('nm:unread', function (e) { setUnread(e.detail); });
  }

  function start() {
    injectStyles();
    if (!build()) { log('could not build launcher'); return; }
    wire();
    // The loader may have reported a count before this script started.
    if (window.__nmUnread !== undefined) setUnread(window.__nmUnread);
    log('ready');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.nmChatDebug = function () {
    var b = window.__nmBot;
    var state = null;
    if (b && typeof b.getState === 'function') {
      try { state = b.getState(); } catch (e) { state = 'threw'; }
    }
    return {
      launcherInDom: !!document.getElementById('nm-chat'),
      sdkLoaded: !!window.RobylonChatbot,
      instanceCaptured: !!b,
      instanceUsable: !!bot(),
      instanceKeys: b ? Object.keys(b) : [],
      botState: state,
      chatOpen: chatOpen,
      unread: unread
    };
  };

  /* Test the badge without waiting for a real message: nmChatTestBadge(3) */
  window.nmChatTestBadge = function (n) { setUnread(n); };
})();
